/**
 * Input contract for the `setUserPhotoOnBehalf` callable function.
 *
 * Uploads a photo for a user OR a contact. Exactly one of `userId` or
 * `contactId` must be provided.
 *
 * For users: organizer must be in an event the target user is a member of
 * (or pass eventId="__global__" as a global admin). The photo is stored at
 * `users/{userId}/profile/` and `users.photoURL` is updated.
 *
 * For contacts: caller must be organizer/admin (global check — contacts
 * aren't event-scoped). The photo is stored at `contacts/{contactId}/profile/`
 * and `contacts.photoURL` is updated.
 *
 * The image bytes are passed as base64 in the request payload. The callable
 * validates content type, size, and authorization, then uploads via Admin SDK
 * (which bypasses storage rules).
 */

import { z } from 'zod';

export const setUserPhotoOnBehalfInputSchema = z
  .object({
    /** Event the organizer is acting within. "__global__" for global user page. Required when userId is set. */
    eventId: z.string().min(1).optional(),
    /** Target user whose photoURL is being set. Mutually exclusive with contactId. */
    userId: z.string().min(1).optional(),
    /** Target contact whose photoURL is being set. Mutually exclusive with userId. */
    contactId: z.string().min(1).optional(),
    /** Base64-encoded image bytes (no data URI prefix). */
    imageBase64: z.string().min(1),
    /** MIME type — must match the image/.* pattern. */
    contentType: z.string().min(1),
  })
  .strict()
  .refine((data) => Boolean(data.userId) !== Boolean(data.contactId), {
    message: 'Exactly one of userId or contactId is required.',
  });

export type SetUserPhotoOnBehalfInput = z.infer<
  typeof setUserPhotoOnBehalfInputSchema
>;

export interface SetUserPhotoOnBehalfResponse {
  success: true;
  /** The userId or contactId whose photoURL was set. */
  userId?: string;
  contactId?: string;
  /** Resolved download URL written to the document. */
  photoURL: string;
}
